import { observable, action, computed } from "mobx";
import MediaItem from "~/models/Media/MediaItem";
import FormStore from "~/stores/FormStore";
import MediaItemDownloadRepository from "~/repository/Media/MediaItemDownloadRepository";
import MediaClassName from "~/constants/Media/MediaItemClassName";
import MediaItemDownloadInfoSize from "~/constants/Media/MediaItemDownloadInfoSize";

const downloadSizeMap = {
  smallPhoto: 0.1,
  largePhoto: 0.5,
  mlsPhoto: 0.1,
  printQualityPhoto: 4.0,
  smallVirtualTour: 0.5,
  largeVirtualTour: 3.0,
  smallVideoClip: 5.0,
  largeVideoClip: 25.0,
  fhdVideoClip: 45.0,
};

class MediaItemDownloadStore {
  @observable isLoaded = false;

  @observable isLoading = false;

  @observable mediaItems = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable smallPhotos = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable largePhotos = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable mlsPhotos = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable printQualityPhotos = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable smallVirtualTours = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable largeVirtualTours = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable smallVideoClips = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable largeVideoClips = [];

  /**
   * @property {MediaItemDownloadInfo[]}
   */
  @observable fhdVideoClips = [];

  /**
   * @property {FormStore}
   */
  @observable formStore = null;

  @observable filesSelectedCount = 0;

  /**
   * @property {number}
   */
  @observable downloadSize = 0;

  /**
   * @property {string}
   */
  @observable zipFilename = null;

  /**
   * @property {string}
   */
  @observable zipDownloadUrl = null;

  /**
   * @property {string}
   */
  @observable flyerUrl = null;

  /**
   * @property {string}
   */
  exportedTourVideoBrandedUrl = null;

  /**
   * @property {string}
   */
  exportedTourVideoUnbrandedUrl = null;

  mediaItemDownloadRepository = null;

  /**
   * @property {string|null}
   */
  token = null;

  constructor() {
    this.mediaItemDownloadRepository = new MediaItemDownloadRepository();
    this.formStore = new FormStore();
  }

  @action
  load(tourId) {
    this.isLoading = true;

    let query = null;

    if (this.token) {
      query = { token: this.token };
    }

    return this.mediaItemDownloadRepository
      .fetch(tourId, query)
      .then(response => {
        const {
          zipDownloadUrl,
          zipFilename,
          flyerUrl,
          exportedTourVideoUnbrandedUrl,
          exportedTourVideoBrandedUrl,
          mediaItems,
        } = response.data;

        const withToken = this.token ? `?token=${this.token}` : "";

        this.zipDownloadUrl = `${zipDownloadUrl}${withToken}`;
        this.zipFilename = zipFilename;
        this.flyerUrl = flyerUrl;
        this.exportedTourVideoBrandedUrl = exportedTourVideoBrandedUrl;
        this.exportedTourVideoUnbrandedUrl = exportedTourVideoUnbrandedUrl;

        this.receiveMediaItems(mediaItems);
        this.initializeForm();

        this.isLoaded = true;
      })
      .finally(() => {
        this.isLoading = false;
      });
  }

  @action
  receiveMediaItems(mediaItems) {
    mediaItems.forEach(mediaItemData => {
      const mediaItem = new MediaItem(mediaItemData);
      this.mediaItems.push(mediaItem);

      switch (mediaItem.className) {
        case MediaClassName.PHOTO:
          this.receivePhoto(mediaItem);
          break;
        case MediaClassName.VIRTUAL_TOUR:
          this.receiveVirtualTour(mediaItem);
          break;
        case MediaClassName.VIDEO_CLIP:
          this.receiveVideoClip(mediaItem);
          break;
        default:
          throw new Error(`Unexpected media className: ${mediaItem.className}`);
      }
    });
  }

  @action
  receivePhoto(photo) {
    photo.downloads.forEach(download => {
      switch (download.size) {
        case MediaItemDownloadInfoSize.SMALL:
          this.smallPhotos.push(download);
          break;
        case MediaItemDownloadInfoSize.LARGE:
          this.largePhotos.push(download);
          break;
        case MediaItemDownloadInfoSize.MLS:
          this.mlsPhotos.push(download);
          break;
        case MediaItemDownloadInfoSize.PRINT:
          this.printQualityPhotos.push(download);
          break;
        default:
          throw new Error(`Unexpected Photo download info size: ${download.size}`);
      }
    });
  }

  @action
  receiveVirtualTour(virtualTour) {
    virtualTour.downloads.forEach(download => {
      switch (download.size) {
        case MediaItemDownloadInfoSize.SMALL:
          this.smallVirtualTours.push(download);
          break;
        case MediaItemDownloadInfoSize.LARGE:
          this.largeVirtualTours.push(download);
          break;
        default:
          throw new Error(`Unexpected VirtualTour download info size: ${download.size}`);
      }
    });
  }

  @action
  receiveVideoClip(videoClip) {
    videoClip.downloads.forEach(download => {
      switch (download.size) {
        case MediaItemDownloadInfoSize.SD:
          this.smallVideoClips.push(download);
          break;
        case MediaItemDownloadInfoSize.HD:
          this.largeVideoClips.push(download);
          break;
        case MediaItemDownloadInfoSize.FHD:
          this.fhdVideoClips.push(download);
          break;
        default:
          throw new Error(`Unexpected VideoClip download info size: ${download.size}`);
      }
    });
  }

  @action
  initializeForm() {
    // We'll default checked to true if there are any media to download in the given category.
    // Downstream at the components we'll inspect these checked properties to calculate the average
    // final bulk download size in Megabytes. Those unchecked won't be taken into account.

    const model = {
      // Photos
      smallPhotosChecked: this.smallPhotos.length > 0,
      largePhotosChecked: this.largePhotos.length > 0,
      mlsPhotosChecked: this.mlsPhotos.length > 0,
      printQualityPhotosChecked: this.printQualityPhotos.length > 0,
      // VirtualTours
      smallVirtualToursChecked: this.smallVirtualTours.length > 0,
      largeVirtualToursChecked: this.largeVirtualTours.length > 0,
      // VideoClips
      smallVideoClipsChecked: this.smallVideoClips.length > 0,
      largeVideoClipsChecked: this.largeVideoClips.length > 0,
      fhdVideoClipsChecked: this.fhdVideoClips.length > 0,
      useShortFilenamesChecked: true,
    };

    this.formStore = new FormStore(model);
    this.formStore.addChangeHandler(this.calculateDownloadSummary.bind(this));
  }

  @action
  calculateDownloadSummary(model) {
    const smallPhotosSelected = (model.smallPhotosChecked ? 1 : 0) * this.smallPhotos.length;
    const largePhotosSelected = (model.largePhotosChecked ? 1 : 0) * this.largePhotos.length;
    const mlsPhotosSelected = (model.mlsPhotosChecked ? 1 : 0) * this.mlsPhotos.length;
    const printQualityPhotosSelected = (model.printQualityPhotosChecked ? 1 : 0) * this.printQualityPhotos.length;
    const smallVirtualToursSelected = (model.smallVirtualToursChecked ? 1 : 0) * this.smallVirtualTours.length;
    const largeVirtualToursSelected = (model.largeVirtualToursChecked ? 1 : 0) * this.largeVirtualTours.length;
    const smallVideoClipsSelected = (model.smallVideoClipsChecked ? 1 : 0) * this.smallVideoClips.length;
    const largeVideoClipsSelected = (model.largeVideoClipsChecked ? 1 : 0) * this.largeVideoClips.length;
    const fhdVideoClipsSelected = (model.fhdVideoClipsChecked ? 1 : 0) * this.fhdVideoClips.length;

    this.filesSelectedCount =
      smallPhotosSelected +
      largePhotosSelected +
      mlsPhotosSelected +
      printQualityPhotosSelected +
      smallVirtualToursSelected +
      largeVirtualToursSelected +
      smallVideoClipsSelected +
      largeVideoClipsSelected +
      fhdVideoClipsSelected;

    this.downloadSize =
      smallPhotosSelected * downloadSizeMap.smallPhoto +
      largePhotosSelected * downloadSizeMap.largePhoto +
      mlsPhotosSelected * downloadSizeMap.mlsPhoto +
      printQualityPhotosSelected * downloadSizeMap.printQualityPhoto +
      smallVirtualToursSelected * downloadSizeMap.smallVirtualTour +
      largeVirtualToursSelected * downloadSizeMap.largeVirtualTour +
      smallVideoClipsSelected * downloadSizeMap.smallVideoClip +
      largeVideoClipsSelected * downloadSizeMap.largeVideoClip +
      fhdVideoClipsSelected * downloadSizeMap.fhdVideoClip;
  }

  @computed
  get hasPhotos() {
    return (
      this.smallPhotos.length > 0 ||
      this.largePhotos.length > 0 ||
      this.mlsPhotos.length > 0 ||
      this.printQualityPhotos.length > 0
    );
  }

  @computed
  get hasVirtualTours() {
    return this.smallVirtualTours.length > 0 || this.largeVirtualTours.length > 0;
  }

  @computed
  get hasVideoClips() {
    return this.smallVideoClips.length > 0 || this.largeVideoClips.length > 0 || this.fhdVideoClips.length > 0;
  }
}

export default MediaItemDownloadStore;
